/**
 * メールテンプレート管理用の型定義
 */

// ============================================
// テンプレート種別
// ============================================

/**
 * テンプレートID
 */
export type EmailTemplateId =
  | "welcome"
  | "login_notification"
  | "magic_link"
  | "purchase_receipt"
  | "email_change_verify_current"
  | "email_change_verify_new"
  | "email_change_notification"
  | "email_change_complete"
  | "contact_notification"
  | "contact_auto_reply";

/**
 * テンプレートメタデータ
 */
export interface EmailTemplateMetadata {
  /** テンプレートID */
  id: EmailTemplateId;
  /** 表示名 */
  name: string;
  /** 説明 */
  description: string;
  /** 使用可能な変数 */
  variables: EmailTemplateVariable[];
}

/**
 * テンプレート変数
 */
export interface EmailTemplateVariable {
  /** 変数名 */
  name: string;
  /** 表示名 */
  label: string;
  /** 説明 */
  description: string;
  /** サンプル値 */
  sampleValue: string;
  /** 必須かどうか */
  required: boolean;
}

// ============================================
// テンプレートデータ
// ============================================

/**
 * メールテンプレート
 */
export interface EmailTemplate {
  /** テンプレートID */
  id: EmailTemplateId;
  /** 表示名 */
  name: string;
  /** 説明 */
  description: string;
  /** 件名テンプレート */
  subject: string;
  /** HTML コンテンツ */
  htmlContent: string;
  /** テキストコンテンツ */
  textContent: string;
  /** 使用可能な変数 */
  variables: EmailTemplateVariable[];
  /** 更新日時 */
  updatedAt: string;
  /** 更新者 */
  updatedBy: string | null;
  /** カスタマイズ済みかどうか */
  isCustomized: boolean;
}

/**
 * テンプレート一覧のレスポンス
 */
export interface EmailTemplateListResponse {
  templates: EmailTemplate[];
}

/**
 * テンプレート更新リクエスト
 */
export interface EmailTemplateUpdateRequest {
  /** 件名テンプレート */
  subject: string;
  /** HTML コンテンツ */
  htmlContent: string;
  /** テキストコンテンツ */
  textContent: string;
}

/**
 * テンプレートプレビューリクエスト
 */
export interface EmailTemplatePreviewRequest {
  /** 変数値 */
  variables: Record<string, string>;
}

/**
 * テンプレートプレビューレスポンス
 */
export interface EmailTemplatePreviewResponse {
  /** 展開後の件名 */
  subject: string;
  /** 展開後の HTML */
  html: string;
  /** 展開後のテキスト */
  text: string;
}

// ============================================
// テンプレートメタデータ定義
// ============================================

/**
 * 全テンプレートのメタデータ
 */
export const EMAIL_TEMPLATE_METADATA: Record<EmailTemplateId, EmailTemplateMetadata> = {
  welcome: {
    id: "welcome",
    name: "ウェルカムメール",
    description: "新規登録時に送信されるメール",
    variables: [
      {
        name: "userName",
        label: "ユーザー名",
        description: "登録したユーザーの名前",
        sampleValue: "田中太郎",
        required: true,
      },
    ],
  },
  login_notification: {
    id: "login_notification",
    name: "ログイン通知",
    description: "ログイン時に送信される通知メール",
    variables: [
      {
        name: "userName",
        label: "ユーザー名",
        description: "ログインしたユーザーの名前",
        sampleValue: "田中太郎",
        required: true,
      },
      {
        name: "loginTime",
        label: "ログイン日時",
        description: "ログインした日時",
        sampleValue: "2025/12/31 14:30",
        required: true,
      },
    ],
  },
  magic_link: {
    id: "magic_link",
    name: "マジックリンク",
    description: "パスワードレスログイン用のリンクメール",
    variables: [
      {
        name: "magicLinkUrl",
        label: "ログインURL",
        description: "マジックリンクのURL",
        sampleValue: "https://example.com/auth/verify?token=xxx",
        required: true,
      },
      {
        name: "expiresInMinutes",
        label: "有効期限（分）",
        description: "リンクの有効期限",
        sampleValue: "15",
        required: true,
      },
    ],
  },
  purchase_receipt: {
    id: "purchase_receipt",
    name: "購入領収書",
    description: "プレミアム会員購入時の領収書メール",
    variables: [
      {
        name: "userName",
        label: "ユーザー名",
        description: "購入したユーザーの名前",
        sampleValue: "田中太郎",
        required: true,
      },
      {
        name: "amount",
        label: "購入金額",
        description: "支払った金額",
        sampleValue: "¥500",
        required: true,
      },
      {
        name: "purchaseDate",
        label: "購入日",
        description: "購入した日付",
        sampleValue: "2025/12/31",
        required: true,
      },
    ],
  },
  email_change_verify_current: {
    id: "email_change_verify_current",
    name: "メール変更確認（現在）",
    description: "メールアドレス変更時に現在のメールに送信される確認メール",
    variables: [
      {
        name: "verifyUrl",
        label: "確認URL",
        description: "メール変更を確認するURL",
        sampleValue: "https://example.com/auth/email-change/verify?token=xxx",
        required: true,
      },
    ],
  },
  email_change_verify_new: {
    id: "email_change_verify_new",
    name: "メール変更確認（新規）",
    description: "メールアドレス変更時に新しいメールに送信される確認メール",
    variables: [
      {
        name: "verifyUrl",
        label: "確認URL",
        description: "新メールアドレスを確認するURL",
        sampleValue: "https://example.com/auth/email-change/verify?token=xxx",
        required: true,
      },
    ],
  },
  email_change_notification: {
    id: "email_change_notification",
    name: "メール変更通知",
    description: "メールアドレス変更のスケジュール通知メール",
    variables: [
      {
        name: "newEmail",
        label: "新メールアドレス",
        description: "変更後のメールアドレス",
        sampleValue: "new@example.com",
        required: true,
      },
      {
        name: "cancelUrl",
        label: "キャンセルURL",
        description: "変更をキャンセルするURL",
        sampleValue: "https://example.com/auth/email-change/cancel?token=xxx",
        required: true,
      },
      {
        name: "approveUrl",
        label: "承認URL",
        description: "今すぐ変更を承認するURL",
        sampleValue: "https://example.com/auth/email-change/approve?token=xxx",
        required: true,
      },
      {
        name: "scheduledAt",
        label: "予定日時",
        description: "自動変更が実行される予定日時",
        sampleValue: "2026/01/03 14:30",
        required: true,
      },
    ],
  },
  email_change_complete: {
    id: "email_change_complete",
    name: "メール変更完了",
    description: "メールアドレス変更完了通知メール",
    variables: [
      {
        name: "oldEmail",
        label: "旧メールアドレス",
        description: "変更前のメールアドレス",
        sampleValue: "old@example.com",
        required: true,
      },
      {
        name: "newEmail",
        label: "新メールアドレス",
        description: "変更後のメールアドレス",
        sampleValue: "new@example.com",
        required: true,
      },
    ],
  },
  contact_notification: {
    id: "contact_notification",
    name: "問い合わせ通知（運営）",
    description: "ユーザーからの問い合わせを運営に通知するメール",
    variables: [
      {
        name: "contactId",
        label: "問い合わせID",
        description: "問い合わせの識別ID",
        sampleValue: "abc123",
        required: true,
      },
      {
        name: "categoryLabel",
        label: "カテゴリ",
        description: "問い合わせのカテゴリ",
        sampleValue: "バグ報告",
        required: true,
      },
      {
        name: "contactName",
        label: "名前",
        description: "問い合わせ者の名前",
        sampleValue: "田中太郎",
        required: false,
      },
      {
        name: "contactEmail",
        label: "メールアドレス",
        description: "問い合わせ者のメールアドレス",
        sampleValue: "user@example.com",
        required: true,
      },
      {
        name: "contactSubject",
        label: "件名",
        description: "問い合わせの件名",
        sampleValue: "ボタンが動作しません",
        required: true,
      },
      {
        name: "contactMessage",
        label: "本文",
        description: "問い合わせの本文",
        sampleValue: "ログインボタンをクリックしても反応がありません。",
        required: true,
      },
      {
        name: "createdAt",
        label: "受信日時",
        description: "問い合わせを受信した日時",
        sampleValue: "2025/12/31 14:30",
        required: true,
      },
      {
        name: "ipAddress",
        label: "IPアドレス",
        description: "問い合わせ者のIPアドレス",
        sampleValue: "192.168.1.1",
        required: false,
      },
    ],
  },
  contact_auto_reply: {
    id: "contact_auto_reply",
    name: "問い合わせ自動返信",
    description: "問い合わせ受付時にユーザーに送信される自動返信メール",
    variables: [
      {
        name: "greeting",
        label: "敬称",
        description: "宛名の敬称（○○様 または お客様）",
        sampleValue: "田中太郎様",
        required: true,
      },
      {
        name: "subject",
        label: "件名",
        description: "問い合わせの件名",
        sampleValue: "ボタンが動作しません",
        required: true,
      },
    ],
  },
};
